import { ParentProps, createContext, createSignal, createEffect } from 'solid-js'
import { requireContext } from 'common'
import { TwoPointSliderContext } from './TwoPointSliderContext'
import styles from './sis-two-point-slider.module.css'
import SISTwoPointSliderApi from '../../api/sis-two-point-slider'
import { STComponentWrapperProps, requireViewContext } from 'st-app'
import { ViewContext } from 'st-app/components/st-view/ViewContext'
import { TwoPointSliderValue } from '../../api'
import { createStore } from 'solid-js/store'

const TwoPointSliderContextJsx = createContext<TwoPointSliderContext>();
export const requireTwoPointSliderContext = () => {
    return requireContext(TwoPointSliderContextJsx);
}

export class TwoPointSliderProps extends STComponentWrapperProps {
    
}

export default function TwoPointSlider(props: ParentProps<TwoPointSliderProps>) {
    const context = new TwoPointSliderContext();

    return (
        <TwoPointSliderContextJsx.Provider value={context}>
            <TwoPointSliderInternal {...props} />
        </TwoPointSliderContextJsx.Provider>
    )
}

type DragTarget = 'low' | 'high' | null;

function TwoPointSliderInternal(props: ParentProps<TwoPointSliderProps>) {
    const [lowOldValue, setLowOldValue] = createSignal<number | undefined>(undefined);
    const [highOldValue, setHighOldValue] = createSignal<number | undefined>(undefined);
    const [isDragging, setIsDragging] = createSignal<DragTarget>(null);
    const [shouldUpdate, setShouldUpdate] = createSignal<boolean>(false);
    const [slideProps, setSlideProps] = createStore<TwoPointSliderValue>(new TwoPointSliderValue())

    let sliderRef: HTMLDivElement | undefined;
    const viewCtx: ViewContext = requireViewContext()

    createEffect(() => {
        if (props.value)
        {
            SISTwoPointSliderApi.DeserializeProps(props.value).then(p => {
                if (lowOldValue() == undefined) setLowOldValue(p.lowValue);
                if (highOldValue() == undefined) setHighOldValue(p.highValue);

                if (p.lowValue < p.minValue) {
                    p.lowValue = p.minValue
                }

                if (p.lowValue > p.maxValue) {
                    p.lowValue = roundToStep(p.maxValue - p.step, p.step)
                }

                if (p.highValue > p.maxValue) {
                    p.highValue = p.maxValue
                }

                if (p.highValue < p.minValue) {
                    p.highValue = roundToStep(p.minValue + p.step, p.step)
                }

                setSlideProps(p)
            });
        }
        else
        {
            if (lowOldValue())
            {
                setSlideProps("lowValue", lowOldValue()!);
            }

            if (highOldValue())
            {
             setSlideProps("highValue", highOldValue()!);
            }
        }
    });

    const roundToStep = (value: number, step: number) => {
        const rounded = Math.round(value / step) * step;
        // Fix floating point precision issues
        const decimals = (step.toString().split('.')[1] || '').length;
        return parseFloat(rounded.toFixed(decimals));
    }

    const getPercentage = (value: number) => ((value - slideProps.minValue) / (slideProps.maxValue - slideProps.minValue)) * 100;

    const getValueFromPosition = (clientX: number) => {
        if (!sliderRef) return slideProps.minValue;

        const rect = sliderRef.getBoundingClientRect();
        const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const rawValue = slideProps.minValue + percentage * (slideProps.maxValue - slideProps.minValue)
        return roundToStep(rawValue, slideProps.step);
    };

    const updateValues = (newLow: number, newHigh: number) => {

        if (slideProps.enforceInteger) {
            newLow = Math.floor(newLow)
            newHigh = Math.floor(newHigh)
        }

        if (newLow != slideProps.lowValue || newHigh != slideProps.highValue) {
            setShouldUpdate(true)
            setSlideProps({ ...slideProps, highValue: newHigh, lowValue: newLow })
        }
    };

    const updateBackend = () => {
        if (shouldUpdate()) {
            viewCtx.events.setValue(JSON.stringify({ ...slideProps, highValue: slideProps.highValue, lowValue: slideProps.lowValue }));
            setShouldUpdate(false)
        }
    }

    const handleMouseDown = (thumb: DragTarget) => (e: MouseEvent) => {
        e.preventDefault();
        setIsDragging(thumb);

        const handleMouseMove = (e: MouseEvent) => {
            const newValue = getValueFromPosition(e.clientX);

            if (thumb === 'low') {
                const newLow = Math.max(slideProps.minValue, Math.min(newValue, slideProps.maxValue));
                // If moving low thumb would make it higher than high thumb, swap them
                if (newLow > slideProps.highValue) {
                    updateValues(slideProps.highValue, newLow);
                } else {
                    updateValues(newLow, slideProps.highValue);
                }
            } else {
                const newHigh = Math.max(slideProps.minValue, Math.min(newValue, slideProps.maxValue));
                // If moving high thumb would make it lower than low thumb, swap them
                if (newHigh < slideProps.lowValue) {
                    updateValues(newHigh, slideProps.lowValue);
                } else {
                    updateValues(slideProps.lowValue, newHigh);
                }
            }
        };

        const handleMouseUp = () => {
            setIsDragging(null);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            updateBackend()
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleSliderClick = (e: MouseEvent) => {
        if (isDragging()) return;

        const newValue = getValueFromPosition(e.clientX);
        const lowDist = Math.abs(newValue - slideProps.lowValue);
        const highDist = Math.abs(newValue - slideProps.highValue);

        if (lowDist < highDist) {
            // Moving low thumb
            if (newValue > slideProps.highValue) {
                updateValues(slideProps.highValue, newValue);
            } else {
                updateValues(newValue, slideProps.highValue);
            }
        } else {
            // Moving high thumb
            if (newValue < slideProps.lowValue) {
                updateValues(newValue, slideProps.lowValue);
            } else {
                updateValues(slideProps.lowValue, newValue);
            }
        }

        updateBackend()
    };

    const handleInputChange = (thumb: DragTarget) => (e: InputEvent) => {
        const targetElm = e.target as HTMLInputElement
        const value = parseFloat(targetElm.value);

        if (!isNaN(value)) {
            // Clamp value to slider bounds
            const clampedValue = Math.max(slideProps.minValue, Math.min(value, slideProps.maxValue));

            if (thumb === 'low') {
                // If new low value would be higher than current high, swap them
                if (clampedValue > slideProps.highValue) {
                    updateValues(slideProps.highValue, clampedValue);
                } else {
                    updateValues(clampedValue, slideProps.highValue);
                }
            } else {
                // If new high value would be lower than current low, swap them
                if (clampedValue < slideProps.lowValue) {
                    updateValues(clampedValue, slideProps.lowValue);
                } else {
                    updateValues(slideProps.lowValue, clampedValue);
                }
            }

            updateBackend()
        }
    };

    const handleInputBlur = (thumb: DragTarget) => (e: FocusEvent) => {
        const targetElm = e.target as HTMLInputElement
        targetElm.value = (thumb === 'low' ? slideProps.lowValue : slideProps.highValue).toString();
    };

    const handleInputKeyDown = (e: KeyboardEvent) => {
        const targetElm = e.target as HTMLInputElement
        if (e.key === 'Enter') {
            targetElm.blur();
        }
    };

    return (
        <div class={styles['sis-two-point-slider']} style="width: 100%;">
            <span class={styles['slider-title']}>{slideProps.title}</span>
            {/* Value Labels - Aligned to slider ends */}
            <div class={styles["value-labels"]}>

                {/* Legend */}
                <div class={styles["legend"]}>
                    <div class={styles["legend-item"]}>
                        <div class={`${styles["legend-color"]} ${styles["legend-low"]}`} style="background-color: #ef4444;"></div>
                        <span class={styles["legend-text"]}>{slideProps.lowBandLegend} {slideProps.lowValue}</span>
                    </div>
                    <div class={styles["legend-item"]}>
                        <div class={`${styles["legend-color"]} ${styles["legend-mid"]}`} style="background-color: #fbbf24;"></div>
                        <span class={styles["legend-text"]}>{slideProps.midBandLegend} {slideProps.lowValue} - {slideProps.highValue}</span>
                    </div>
                    <div class={styles["legend-item"]}>
                        <div class={`${styles["legend-color"]} ${styles['legend-high']}`} style="background-color: #10b981;"></div>
                        <span class={styles["legend-text"]}>{slideProps.highBandLegend} {slideProps.highValue}</span>
                    </div>
                </div>

            </div>
            <div class={`${styles['slider-detail']} ${isDragging() ? styles['active'] : ''}`}>
                <div class={styles['slider-wrapper']}>
                    {/* Slider Track */}
                    <div
                        ref={sliderRef}
                        class={styles['slider-track']}
                        onMouseDown={handleSliderClick}
                    >
                        {/* Red Band */}
                        <div
                            class={`${styles['slider-band']} ${styles['band-red']}`}
                            style={`width: ${getPercentage(slideProps.lowValue)}%`}
                        />

                        {/* Yellow Band */}
                        <div
                            class={`${styles['slider-band']} ${styles['band-yellow']}`}
                            style={`left: ${getPercentage(slideProps.lowValue)}%; width: ${getPercentage(slideProps.highValue) - getPercentage(slideProps.lowValue)}%`}
                        />

                        {/* Green Band */}
                        <div
                            class={`${styles['slider-band']} ${styles['band-green']}`}
                            style={`width: ${100 - getPercentage(slideProps.highValue)}%`}
                        />
                    </div>

                    {/* Low Thumb */}
                    <div
                        class={`${styles['slider-thumb']} ${isDragging() === 'low' ? styles['dragging'] : ''}`}
                        style={`left: ${getPercentage(slideProps.lowValue)}%`}
                        onMouseDown={handleMouseDown('low')}
                    >
                        <div class={styles["thumb-tooltip"]}>
                            <input
                                class={styles['thumb-input']}
                                type="text"
                                value={slideProps.lowValue}
                                min={slideProps.minValue}
                                max={slideProps.highValue - 1}
                                onInput={handleInputChange('low')}
                                onBlur={handleInputBlur('low')}
                                onKeyDown={handleInputKeyDown}
                                onMouseDown={(e: MouseEvent) => { e.stopPropagation() }}
                            />
                        </div>
                    </div>

                    {/* High Thumb */}
                    <div
                        class={`${styles['slider-thumb']} ${isDragging() === 'high' ? styles['dragging'] : ''}`}
                        style={`left: ${getPercentage(slideProps.highValue)}%`}
                        onMouseDown={handleMouseDown('high')}
                    >
                        <div class={styles["thumb-tooltip"]}>
                            <input
                                class={styles['thumb-input']}
                                type='text'
                                value={slideProps.highValue}
                                min={slideProps.lowValue + 1}
                                max={slideProps.maxValue}
                                onInput={handleInputChange('high')}
                                onBlur={handleInputBlur('high')}
                                onKeyDown={handleInputKeyDown}
                                onMouseDown={(e: MouseEvent) => { e.stopPropagation() }}
                            />
                        </div>
                    </div>
                </div>
                <div class={styles["range-wrapper"]}>
                    <span class={styles["min-label"]}>{slideProps.minValue}</span>
                    <span class={styles["max-label"]}>{slideProps.maxValue}</span>
                </div>
            </div>
        </div>
    );
}

TwoPointSlider.defaultProps = new TwoPointSliderProps();
